import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Menu,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import GroupMenu, { Dot } from '@/components/researcher/respondentBase/GroupMenu'
import InviteToStudyDialog from '@/components/researcher/respondentBase/InviteToStudyDialog'
import PasteComposer from '@/components/researcher/respondentBase/PasteComposer'
import SelectionBar from '@/components/researcher/respondentBase/SelectionBar'
import { hueFor, initialsFor, statusColor } from '@/components/researcher/respondentBase/visuals'
import {
  respondentBaseService,
  type AddressBookRespondent,
  type RespondentTag,
} from '@/services/respondentBaseService'

/** Group pills shown before the rest fold into "+n". */
const VISIBLE_GROUPS = 4

export default function RespondentBase() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t, i18n } = useTranslation('respondents')

  const [respondents, setRespondents] = useState<AddressBookRespondent[] | null>(null)
  const [groups, setGroups] = useState<RespondentTag[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [groupFilter, setGroupFilter] = useState<number | 'all'>('all')
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null)

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [groupAnchor, setGroupAnchor] = useState<HTMLElement | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [editing, setEditing] = useState<AddressBookRespondent | null>(null)

  const load = async () => {
    try {
      const [people, tagList] = await Promise.all([
        respondentBaseService.listRespondents(),
        respondentBaseService.listTags(),
      ])
      setRespondents(people)
      setGroups(tagList)
      // Drop selections that no longer exist (deleted elsewhere, or just now).
      setSelected((current) => new Set([...current].filter((id) => people.some((p) => p.id === id))))
    } catch (err) {
      setRespondents((current) => current ?? [])
      setError(err instanceof Error ? err.message : t('errors.load'))
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => b.respondent_count - a.respondent_count || a.name.localeCompare(b.name)),
    [groups],
  )
  const pillGroups = useMemo(() => {
    const head = sortedGroups.slice(0, VISIBLE_GROUPS)
    // A filter picked from the overflow menu stays visible as a pill.
    const active = sortedGroups.find((g) => g.id === groupFilter)
    return active && !head.includes(active) ? [...head, active] : head
  }, [sortedGroups, groupFilter])
  const overflowGroups = sortedGroups.filter((g) => !pillGroups.includes(g))

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (respondents ?? []).filter((r) => {
      const matchesSearch = !q || r.email.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)
      const matchesGroup = groupFilter === 'all' || r.tags.some((tg) => tg.id === groupFilter)
      return matchesSearch && matchesGroup
    })
  }, [respondents, search, groupFilter])

  const loading = respondents === null
  const isEmpty = !loading && respondents.length === 0

  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id))
  const someVisibleSelected = filtered.some((r) => selected.has(r.id))

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAllVisible = () =>
    setSelected((current) => {
      const next = new Set(current)
      for (const r of filtered) {
        if (allVisibleSelected) next.delete(r.id)
        else next.add(r.id)
      }
      return next
    })

  const selectedPeople = (respondents ?? []).filter((r) => selected.has(r.id))

  const addSelectedToGroup = async (groupName: string | null) => {
    if (!groupName || selectedPeople.length === 0) return
    try {
      // The bulk endpoint merges tags into existing entries — one call for the whole selection.
      await respondentBaseService.bulkAdd({
        emails: selectedPeople.map((r) => r.email).join('\n'),
        tag_names: [groupName],
      })
      setNotice(t('selection.grouped', { group: groupName }))
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.save'))
    }
  }

  const deleteSelected = async () => {
    setConfirmDelete(false)
    const results = await Promise.allSettled(selectedPeople.map((r) => respondentBaseService.deleteRespondent(r.id)))
    if (results.some((res) => res.status === 'rejected')) setError(t('errors.delete'))
    await load()
  }

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }), [i18n.language])

  const head = {
    fontSize: 12,
    fontWeight: 700,
    color: c.ink56,
    borderBottom: `1px solid ${c.ink08}`,
    py: 1.5,
    whiteSpace: 'nowrap',
  } as const
  const cell = { fontSize: 14, color: c.ink, borderBottom: `1px solid ${c.ink08}`, py: 1.25 } as const
  const cardSx = {
    bgcolor: 'background.paper',
    borderRadius: '16px',
    boxShadow: `0 0 0 1px ${c.ink08}, 0 1px 2px ${c.ink04}`,
  } as const

  return (
    <ResearcherShell active="respondents" crumbs={[t('title')]} hideTopBarAction>
      <Box sx={{ maxWidth: 1340, mx: 'auto', pb: selected.size ? 12 : 0 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 26, md: 34 }, lineHeight: 1.08, letterSpacing: '-0.02em' }}>
            {t('title')}
          </Typography>
          <Typography sx={{ fontSize: 15, color: c.ink56, mt: 0.75 }}>{t('subtitle')}</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : isEmpty ? (
          <Box sx={{ ...cardSx, px: 3, py: { xs: 6, md: 9 }, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: { xs: 22, md: 26 }, letterSpacing: '-0.015em' }}>
              {t('empty.title')}
            </Typography>
            <Typography sx={{ fontSize: 15, color: c.ink56, mt: 1, mb: 3.5, mx: 'auto', maxWidth: 460 }}>{t('empty.body')}</Typography>
            <Box sx={{ maxWidth: 640, mx: 'auto', textAlign: 'left' }}>
              <PasteComposer large groups={sortedGroups} onAdded={async (msg) => { setNotice(msg); await load() }} onError={setError} />
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <PasteComposer groups={sortedGroups} onAdded={async (msg) => { setNotice(msg); await load() }} onError={setError} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Pill on={groupFilter === 'all'} onClick={() => setGroupFilter('all')} label={t('all')} count={respondents.length} />
              {pillGroups.map((g) => (
                <Pill
                  key={g.id}
                  on={groupFilter === g.id}
                  onClick={() => setGroupFilter(groupFilter === g.id ? 'all' : g.id)}
                  label={g.name}
                  count={g.respondent_count}
                  dot={hueFor(g.id)}
                />
              ))}
              {overflowGroups.length > 0 && (
                <Pill on={false} onClick={(e) => setMoreAnchor(e.currentTarget)} label={t('more', { count: overflowGroups.length })} />
              )}
              <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
                {overflowGroups.map((g) => (
                  <MenuItem
                    key={g.id}
                    onClick={() => {
                      setGroupFilter(g.id)
                      setMoreAnchor(null)
                    }}
                    sx={{ fontSize: 14, gap: 1.25 }}
                  >
                    <Dot color={hueFor(g.id)} />
                    {g.name}
                    <Box component="span" sx={{ ml: 'auto', pl: 2, color: c.ink32 }}>{g.respondent_count}</Box>
                  </MenuItem>
                ))}
              </Menu>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('search')}
                sx={{
                  ml: { sm: 'auto' },
                  width: { xs: '100%', sm: 240 },
                  '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: c.ink04, height: 36 },
                  '& fieldset': { border: 0 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: c.ink48 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer sx={{ ...cardSx, overflow: 'hidden' }}>
              <Table sx={{ tableLayout: { xs: 'fixed', sm: 'auto' }, '& td, & th': { px: { xs: 1.25, md: 2 } } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...head, width: 48 }} padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected && !allVisibleSelected}
                        onChange={toggleAllVisible}
                        inputProps={{ 'aria-label': 'select all' }}
                      />
                    </TableCell>
                    <TableCell sx={head}>{t('columns.person')}</TableCell>
                    <TableCell sx={{ ...head, display: { xs: 'none', md: 'table-cell' } }}>{t('columns.groups')}</TableCell>
                    <TableCell sx={{ ...head, display: { xs: 'none', sm: 'table-cell' } }}>{t('columns.lastStudy')}</TableCell>
                    <TableCell sx={{ ...head, display: { xs: 'none', lg: 'table-cell' } }}>{t('columns.responds')}</TableCell>
                    <TableCell sx={{ ...head, display: { xs: 'none', lg: 'table-cell' } }}>{t('columns.added')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ ...cell, color: c.ink48, textAlign: 'center', py: 5, borderBottom: 0 }}>
                        {t('noMatch')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((r) => {
                      const on = selected.has(r.id)
                      return (
                        <TableRow
                          key={r.id}
                          hover
                          selected={on}
                          onClick={() => setEditing(r)}
                          sx={{
                            cursor: 'pointer',
                            '&:last-child td': { borderBottom: 0 },
                            '&.Mui-selected, &.Mui-selected:hover': { bgcolor: alpha(c.coral, 0.045) },
                          }}
                        >
                          <TableCell sx={cell} padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox size="small" checked={on} onChange={() => toggle(r.id)} inputProps={{ 'aria-label': r.email }} />
                          </TableCell>
                          <TableCell sx={cell}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                              <Avatar respondent={r} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: 14, fontWeight: r.name ? 700 : 400, color: r.name ? c.ink : c.ink48 }}>
                                  {r.name || t('noName')}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: 12.5, color: c.ink56 }}>{r.email}</Typography>
                                <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
                                  <LastStudy respondent={r} />
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...cell, display: { xs: 'none', md: 'table-cell' } }}>
                            <Groups tags={r.tags} />
                          </TableCell>
                          <TableCell sx={{ ...cell, display: { xs: 'none', sm: 'table-cell' } }}>
                            <LastStudy respondent={r} />
                          </TableCell>
                          <TableCell sx={{ ...cell, display: { xs: 'none', lg: 'table-cell' }, fontVariantNumeric: 'tabular-nums', color: r.invited_count ? c.ink : c.ink32 }}>
                            {r.invited_count ? `${r.completed_count}/${r.invited_count}` : '–'}
                          </TableCell>
                          <TableCell sx={{ ...cell, display: { xs: 'none', lg: 'table-cell' }, color: c.ink56, whiteSpace: 'nowrap' }}>
                            {dateFmt.format(new Date(r.created_at))}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <SelectionBar
        count={selected.size}
        onGroup={setGroupAnchor}
        onDelete={() => setConfirmDelete(true)}
        onInvite={() => setInviting(true)}
        onClear={() => setSelected(new Set())}
      />
      <GroupMenu
        anchorEl={groupAnchor}
        onClose={() => setGroupAnchor(null)}
        groups={sortedGroups}
        allowNone={false}
        onPick={(name) => void addSelectedToGroup(name)}
      />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('deleteDialog.title', { count: selected.size })}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>{t('deleteDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmDelete(false)}>
            {t('single.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={() => void deleteSelected()}>
            {t('deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {inviting && (
        <InviteToStudyDialog
          respondentIds={[...selected]}
          onClose={() => setInviting(false)}
          onSent={async (summary) => {
            setInviting(false)
            setSelected(new Set())
            setNotice(summary)
            await load()
          }}
          onError={setError}
        />
      )}

      {editing && (
        <RespondentDialog
          respondent={editing}
          groupOptions={sortedGroups.map((g) => g.name)}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
          onError={setError}
        />
      )}

      <Snackbar open={!!notice} autoHideDuration={4000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </ResearcherShell>
  )
}

function Pill({
  on,
  onClick,
  label,
  count,
  dot,
}: {
  on: boolean
  onClick: (e: MouseEvent<HTMLElement>) => void
  label: string
  count?: number
  dot?: string
}) {
  const c = useTheme().affexy.colors
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={on}
      sx={{
        height: 32,
        px: 1.75,
        border: 0,
        borderRadius: '999px',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.875,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        color: on ? '#fff' : c.ink72,
        bgcolor: on ? c.navy : 'transparent',
        boxShadow: on ? 'none' : `inset 0 0 0 1px ${c.ink08}`,
        transition: 'background-color 120ms ease-out, color 120ms ease-out, transform 100ms ease-out',
        '&:hover': { bgcolor: on ? c.navy : c.ink04 },
        '&:active': { transform: 'scale(0.97)' },
      }}
    >
      {dot && <Dot color={dot} />}
      {label}
      {count !== undefined && (
        <Box component="span" sx={{ fontWeight: 400, color: on ? alpha('#fff', 0.55) : c.ink32 }}>
          {count}
        </Box>
      )}
    </Box>
  )
}

function Avatar({ respondent }: { respondent: AddressBookRespondent }) {
  const c = useTheme().affexy.colors
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        bgcolor: respondent.name ? hueFor(respondent.id) : c.ink32,
      }}
    >
      {initialsFor(respondent.name, respondent.email)}
    </Box>
  )
}

function GroupChip({ tag }: { tag: RespondentTag }) {
  const c = useTheme().affexy.colors
  return (
    <Box
      component="span"
      sx={{
        height: 24,
        px: 1.25,
        borderRadius: '999px',
        bgcolor: c.ink04,
        color: c.ink72,
        fontSize: 12,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      <Dot color={hueFor(tag.id)} />
      {tag.name}
    </Box>
  )
}

function Groups({ tags }: { tags: RespondentTag[] }) {
  const c = useTheme().affexy.colors
  if (tags.length === 0) return <Box component="span" sx={{ color: c.ink32 }}>—</Box>
  const [first, ...rest] = tags
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <GroupChip tag={first} />
      {rest.length > 0 && (
        <Tooltip title={rest.map((tg) => tg.name).join(', ')}>
          <Box component="span" sx={{ height: 24, px: 1, borderRadius: '999px', bgcolor: c.ink04, color: c.ink72, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
            +{rest.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}

function LastStudy({ respondent }: { respondent: AddressBookRespondent }) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('respondents')
  const last = respondent.last_study
  if (!last) return <Typography sx={{ fontSize: 13, color: c.ink48 }}>{t('status.never')}</Typography>
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, minWidth: 0 }}>
      <Dot color={statusColor(last.status)} />
      <Typography noWrap sx={{ fontSize: 13, color: c.ink72 }}>
        {last.project_name} · {t(`status.${last.status}`)}
      </Typography>
    </Box>
  )
}

function RespondentDialog({
  respondent,
  groupOptions,
  onClose,
  onSaved,
  onError,
}: {
  respondent: AddressBookRespondent
  groupOptions: string[]
  onClose: () => void
  onSaved: () => void | Promise<void>
  onError: (msg: string) => void
}) {
  const { t } = useTranslation('respondents')
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [email, setEmail] = useState(respondent.email)
  const [name, setName] = useState(respondent.name)
  const [groupNames, setGroupNames] = useState<string[]>(respondent.tags.map((tg) => tg.name))
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!email.trim()) return
    setSaving(true)
    try {
      await respondentBaseService.updateRespondent(respondent.id, {
        email: email.trim(),
        name: name.trim(),
        tag_names: groupNames,
      })
      await onSaved()
    } catch (err) {
      onError(err instanceof Error ? err.message : t('errors.save'))
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('single.editTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label={t('single.email')} value={email} onChange={(e) => setEmail(e.target.value)} type="email" fullWidth />
        <TextField
          label={t('single.name')}
          placeholder={t('single.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
        />
        <Autocomplete
          multiple
          freeSolo
          options={groupOptions}
          value={groupNames}
          onChange={(_e, value) => setGroupNames(value.map((v) => v.trim()).filter(Boolean))}
          renderInput={(params) => <TextField {...params} label={t('single.groups')} helperText={t('single.groupsHint')} />}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          {t('single.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving || !email.trim()}>
          {saving ? <CircularProgress size={18} color="inherit" /> : t('single.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
